import type { PaginationState } from 'redux/reusable/pagination';
import { createPaginationModule } from 'redux/reusable/pagination';
import type { XhrStatus } from 'redux/reusable/xhrStatus';
import { createXhrStatusModule } from 'redux/reusable/xhrStatus';
import type {
  InvoiceDraft,
  InvoicePreviewCreationResponse,
  InvoiceStatusFilterKeys,
  RawInvoice,
  RawInvoiceRules,
} from 'types/invoice';
import { actionCreatorFactory } from 'typescript-fsa';

export const invoiceXhrModule = createXhrStatusModule('invoice');

const actionCreator = actionCreatorFactory('timely/invoice');
export const fetchedInvoices = actionCreator<{ invoices: Array<RawInvoice>; freshState?: boolean }>(
  'FETCHED_INVOICES'
);
export const clearInvoices = actionCreator('CLEAR_INVOICES');
export const clearQuery = actionCreator('CLEAR_QUERY');
export const clearPagination = actionCreator('CLEAR_PAGINATION');
export const fetchedInvoice = actionCreator<RawInvoice>('FETCHED_SINGLE_INVOICE');
export const createdInvoice = actionCreator<RawInvoice>('CREATED_INVOICE');
export const deletedInvoice = actionCreator<{ id: number }>('DELETED_INVOICE');
export const fetchedInvoiceRules = actionCreator<RawInvoiceRules>('FETCHED_INVOICE_RULES');
export const createInvoiceRulesDraft = actionCreator<RawInvoiceRules | undefined>(
  'CREATE_RULES_DRAFT'
);
export const updateInvoiceRulesDraft = actionCreator<Partial<RawInvoiceRules>>(
  'UPDATE_INVOICE_RULES_ DRAFT'
);
export const setInvoiceDraft = actionCreator<RawInvoice>('SET_INVOICE_DRAFT');
export const setInvoicePreview =
  actionCreator<InvoicePreviewCreationResponse>('SET_INVOICE_PREVIEW');
export const updateInvoiceDraft = actionCreator<Partial<InvoiceDraft>>('UPDATE_INVOICE_DRAFT');
export const clearInvoiceDraft = actionCreator('CLEAR_INVOICE_DRAFT');
export const setQuery = actionCreator<Partial<InvoiceQuery>>('SET_QUERY');
export const toggleCurrentInvoiceId = actionCreator<{ id: number | null }>(
  'TOGGLE_CURRENT_INVOICE_ID'
);

export const { withPaginationReducer, paginationActions } =
  createPaginationModule('invoice-pagination');

export const INVOICE_PAGE_LIMIT = 50;
export const SERVER_SIDE_FIELDS = ['total_cost', 'created_at', 'updated_at', 'id'];
export const TEMP_IS_MULTIPLE_CONNECTIONS_ALLOWED = false;

export const setSortBy = actionCreator<{
  field: InvoiceListFields;
  direction?: 'asc' | 'desc';
}>('SET_SORT_BY');

export enum InvoiceListFields {
  ClientName = 'client.name',
  TotalCost = 'total_cost',
  User = 'user.name',
  Status = 'status',
  Integration = 'integration',
  UpdatedAt = 'updated_at',
  DueDate = 'due_date',
  // to is the only value we will definitely have once an invoice is saved, so sort by that
  DateRange = 'to',
}

export interface InvoiceQuery {
  show: 'active' | 'deleted';
  projectIds: Array<number>;
  userIds: Array<number>;
  status: InvoiceStatusFilterKeys;
}

export interface State {
  entities: Record<number, RawInvoice>;
  result: Array<number>;
  rules: RawInvoiceRules | null;
  draft: InvoiceDraft | null;
  currentInvoiceId: number | null;
  rulesDraft: RawInvoiceRules;
  xhrStatus: XhrStatus;
  preview: InvoicePreviewCreationResponse | null;
  pagination: PaginationState;
  query: InvoiceQuery;
  sortBy: {
    field: InvoiceListFields;
    direction?: 'asc' | 'desc';
  };
}
